import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import DRLProgressBars from './DRLProgressBars'

const CATEGORIAS = ['Corporativo', 'Unidades de negocio', 'Financiero', 'Comercial', 'Operaciones', 'Marketing', 'RRHH']
const ESTADOS    = ['Pendiente', 'En revisión', 'Entrega parcial', 'Recibido', 'No aplica', 'No existe']

// ── Estado final: tracking del destino final de cada item ────────────────
// Empieza vacío (null). Se va llenando a medida que se resuelve la pieza
// de info que originalmente no estaba "Recibido".
const ESTADOS_FINAL = ['—', 'Recibido tarde', 'En construcción', 'Construido por NVC', 'Descartado', 'No fue posible']

// Estados finales "definitivos" que requieren justificación obligatoria
const ESTADOS_FINAL_TERMINAL = ['No fue posible', 'Descartado']

const ESTADO_COLORS = {
  'Pendiente':       { bg: '#FEF3C7', text: '#92400E' },
  'En revisión':     { bg: '#DBEAFE', text: '#1E40AF' },
  'Entrega parcial': { bg: '#FEF9C3', text: '#713F12' },
  'Recibido':        { bg: '#D1FAE5', text: '#065F46' },
  'No aplica':       { bg: '#F3F4F6', text: '#6B7280' },
  'No existe':       { bg: '#FEE2E2', text: '#991B1B' },
}

const ESTADO_FINAL_COLORS = {
  '—':                    { bg: '#F3F4F6', text: '#9CA3AF' },
  'Recibido tarde':       { bg: '#DCFCE7', text: '#166534' },
  'En construcción':      { bg: '#DBEAFE', text: '#1E40AF' },
  'Construido por NVC':   { bg: '#D1FAE5', text: '#065F46' },
  'Descartado':           { bg: '#E5E7EB', text: '#374151' },
  'No fue posible':       { bg: '#FEE2E2', text: '#991B1B' },
}

const CAT_ICONS = {
  'Corporativo':       '🎯',
  'Unidades de negocio': '🏢',
  'Financiero':        '💰',
  'Comercial':         '📈',
  'Operaciones':       '⚙️',
  'Marketing':         '📣',
  'RRHH':              '👥',
}

export default function DRLTab({ consultancyId }) {
  const [items, setItems]           = useState([])
  const [loading, setLoading]       = useState(true)
  const [addingCat, setAddingCat]   = useState(null)
  const [newDesc, setNewDesc]       = useState('')
  const [editingId, setEditingId]   = useState(null)
  const [editForm, setEditForm]     = useState({})
  const [viewMode, setViewMode]     = useState('interno')
  const [categorias, setCategorias] = useState(CATEGORIAS)
  const [editingCat, setEditingCat] = useState(null)   // category name being renamed
  const [editCatName, setEditCatName] = useState('')

  useEffect(() => {
    if (consultancyId) fetchItems()
  }, [consultancyId])

  async function fetchItems() {
    setLoading(true)
    const [{ data }, { data: ficha }] = await Promise.all([
      supabase
        .from('solicitudes_informacion')
        .select('*')
        .eq('consultancy_id', consultancyId)
        .order('orden'),
      supabase
        .from('project_ficha')
        .select('drl_cat_order')
        .eq('consultancy_id', consultancyId)
        .maybeSingle(),
    ])
    const fetched = data || []
    setItems(fetched)

    // Build category list:
    // 1. Use saved order from project_ficha if available
    // 2. Otherwise derive from items' minimum orden value
    const savedOrder = ficha?.drl_cat_order ?? []
    const allItemCats = [...new Set(fetched.map(i => i.categoria))]

    let orderedCats
    if (savedOrder.length > 0) {
      // Start with saved order, add any new cats from items not in saved list
      const newCats = allItemCats.filter(c => !savedOrder.includes(c))
      orderedCats = [...savedOrder.filter(c => allItemCats.includes(c) || CATEGORIAS.includes(c)), ...newCats]
    } else {
      // Derive from min orden per category
      const catMinOrden = {}
      for (const item of fetched) {
        const o = item.orden ?? 999
        if (catMinOrden[item.categoria] === undefined || o < catMinOrden[item.categoria])
          catMinOrden[item.categoria] = o
      }
      const itemCats = Object.keys(catMinOrden).sort((a, b) => catMinOrden[a] - catMinOrden[b])
      orderedCats = itemCats
    }
    // Always append standard empty categories not yet in list
    const emptyStandard = CATEGORIAS.filter(c => !orderedCats.includes(c))
    setCategorias([...orderedCats, ...emptyStandard])

    setLoading(false)
  }

  async function saveCatOrder(newOrder) {
    await supabase
      .from('project_ficha')
      .update({ drl_cat_order: newOrder })
      .eq('consultancy_id', consultancyId)
  }

  async function updateEstado(id, estado) {
    await supabase
      .from('solicitudes_informacion')
      .update({ estado, updated_at: new Date().toISOString() })
      .eq('id', id)
    setItems(prev => prev.map(i => i.id === id ? { ...i, estado } : i))
  }

  async function updateEstadoFinal(id, estado_final) {
    // El "—" en UI se persiste como null en DB para mantener la columna limpia
    const dbValue = estado_final === '—' ? null : estado_final
    const item = items.find(i => i.id === id)

    // Si va a estado terminal (No fue posible / Descartado) sin razón → abrir edición
    // y forzar al user a poner una razón antes de poder guardar el cambio.
    if (ESTADOS_FINAL_TERMINAL.includes(dbValue) && !item?.razon_no_cierre?.trim()) {
      setEditingId(id)
      setEditForm({ ...item, estado_final: dbValue })
      return
    }

    await supabase
      .from('solicitudes_informacion')
      .update({ estado_final: dbValue, updated_at: new Date().toISOString() })
      .eq('id', id)
    setItems(prev => prev.map(i => i.id === id ? { ...i, estado_final: dbValue } : i))
  }

  async function addItem(categoria) {
    if (!newDesc.trim()) return
    const maxOrden = Math.max(0, ...items.filter(i => i.categoria === categoria).map(i => i.orden || 0))
    const { data } = await supabase
      .from('solicitudes_informacion')
      .insert({
        consultancy_id: consultancyId,
        categoria,
        descripcion: newDesc.trim(),
        es_estandar: false,
        orden: maxOrden + 1,
      })
      .select()
      .single()
    if (data) setItems(prev => [...prev, data])
    setNewDesc('')
    setAddingCat(null)
  }

  async function deleteItem(id) {
    await supabase.from('solicitudes_informacion').delete().eq('id', id)
    setItems(prev => prev.filter(i => i.id !== id))
  }

  async function moveItem(id, dir) {
    // Work within the same category
    const item = items.find(i => i.id === id)
    if (!item) return
    const catItems = items.filter(i => i.categoria === item.categoria).sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0))
    const idx = catItems.findIndex(i => i.id === id)
    const swapIdx = idx + dir
    if (swapIdx < 0 || swapIdx >= catItems.length) return
    const other = catItems[swapIdx]
    const ordenA = item.orden ?? idx
    const ordenB = other.orden ?? swapIdx
    // Swap orden values
    await Promise.all([
      supabase.from('solicitudes_informacion').update({ orden: ordenB }).eq('id', item.id),
      supabase.from('solicitudes_informacion').update({ orden: ordenA }).eq('id', other.id),
    ])
    setItems(prev => prev.map(i => {
      if (i.id === item.id) return { ...i, orden: ordenB }
      if (i.id === other.id) return { ...i, orden: ordenA }
      return i
    }))
  }

  async function saveEdit() {
    // Validar: si estado final es terminal, razón es obligatoria
    if (ESTADOS_FINAL_TERMINAL.includes(editForm.estado_final) &&
        !editForm.razon_no_cierre?.trim()) {
      alert(`Debes ingresar una razón al marcar este item como "${editForm.estado_final}".\n\nLa razón se va a usar en el reporte de cierre que ve el cliente.`)
      return
    }

    // Normalizar el "—" visual a null
    const payload = {
      ...editForm,
      estado_final: editForm.estado_final === '—' ? null : editForm.estado_final,
      updated_at: new Date().toISOString(),
    }

    const { data } = await supabase
      .from('solicitudes_informacion')
      .update(payload)
      .eq('id', editingId)
      .select()
      .single()
    if (data) setItems(prev => prev.map(i => i.id === editingId ? data : i))
    setEditingId(null)
  }

  async function renameCat(oldName, newName) {
    const trimmed = newName.trim()
    if (!trimmed || trimmed === oldName) { setEditingCat(null); return }
    await supabase
      .from('solicitudes_informacion')
      .update({ categoria: trimmed })
      .eq('consultancy_id', consultancyId)
      .eq('categoria', oldName)
    const newOrder = categorias.map(c => c === oldName ? trimmed : c)
    setItems(prev => prev.map(i => i.categoria === oldName ? { ...i, categoria: trimmed } : i))
    setCategorias(newOrder)
    await saveCatOrder(newOrder)
    setEditingCat(null)
  }

  async function deleteCat(cat) {
    const fallback = categorias.find(c => c !== cat) ?? 'Otro'
    const catItems = items.filter(i => i.categoria === cat)
    if (catItems.length > 0) {
      await supabase
        .from('solicitudes_informacion')
        .update({ categoria: fallback })
        .eq('consultancy_id', consultancyId)
        .eq('categoria', cat)
      setItems(prev => prev.map(i => i.categoria === cat ? { ...i, categoria: fallback } : i))
    }
    const newOrder = categorias.filter(c => c !== cat)
    setCategorias(newOrder)
    await saveCatOrder(newOrder)
  }

  async function moveCat(cat, dir) {
    const idx = categorias.indexOf(cat)
    const newIdx = idx + dir
    if (newIdx < 0 || newIdx >= categorias.length) return
    const next = [...categorias]
    ;[next[idx], next[newIdx]] = [next[newIdx], next[idx]]
    setCategorias(next)
    await saveCatOrder(next)
  }

  // Stats
  const total    = items.length
  const recibido = items.filter(i => i.estado === 'Recibido').length
  const pendiente = items.filter(i => i.estado === 'Pendiente').length
  const pct       = total > 0 ? Math.round((recibido / total) * 100) : 0

  if (loading) return <div style={{ padding: 32, color: '#9CA3AF', textAlign: 'center' }}>Cargando DRL…</div>

  if (viewMode === 'cliente') {
    return <ClientView items={items} onBack={() => setViewMode('interno')} />
  }

  return (
    <div>
      {/* Summary: dual progress bars + vista cliente button */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, alignItems: 'stretch' }}>
        <div style={{ flex: 1 }}>
          <DRLProgressBars items={items} />
        </div>
        <button
          onClick={() => setViewMode('cliente')}
          style={{
            padding: '0 18px', borderRadius: 12, border: '1px solid #6366F1',
            background: '#EEF2FF', color: '#4F46E5', fontSize: 12, fontWeight: 600,
            cursor: 'pointer', whiteSpace: 'nowrap'
          }}
        >👁️ Vista cliente</button>
      </div>

      {/* Categories */}
      {categorias.map((cat, catIdx) => {
        const catItems = items.filter(i => i.categoria === cat).sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0))
        return (
          <div key={cat} style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 16 }}>{CAT_ICONS[cat] ?? '📁'}</span>

              {/* Editable category title */}
              {editingCat === cat ? (
                <>
                  <input
                    autoFocus
                    value={editCatName}
                    onChange={e => setEditCatName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') renameCat(cat, editCatName); if (e.key === 'Escape') setEditingCat(null) }}
                    style={{ fontWeight: 700, fontSize: 14, padding: '2px 8px', borderRadius: 6, border: '1px solid #6366F1', outline: 'none', width: 160 }}
                  />
                  <button onClick={() => renameCat(cat, editCatName)} style={{ padding: '2px 10px', borderRadius: 6, border: 'none', background: '#6366F1', color: '#fff', fontSize: 12, cursor: 'pointer' }}>✓</button>
                  <button onClick={() => setEditingCat(null)} style={{ padding: '2px 8px', borderRadius: 6, border: '1px solid #E5E7EB', background: '#fff', fontSize: 12, cursor: 'pointer', color: '#6B7280' }}>✕</button>
                </>
              ) : (
                <>
                  <span style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>{cat}</span>
                  <span style={{
                    marginLeft: 4, background: '#E5E7EB', borderRadius: 99,
                    padding: '1px 8px', fontSize: 11, color: '#6B7280', fontWeight: 600
                  }}>{catItems.length}</span>
                  {/* Section reorder */}
                  <div style={{ display: 'flex', gap: 2 }}>
                    <button
                      onClick={() => moveCat(cat, -1)}
                      disabled={catIdx === 0}
                      title="Subir sección"
                      style={{ padding: '2px 7px', borderRadius: 4, border: '1px solid #E5E7EB', background: catIdx === 0 ? '#F9FAFB' : '#fff', fontSize: 11, cursor: catIdx === 0 ? 'default' : 'pointer', color: catIdx === 0 ? '#D1D5DB' : '#6B7280' }}
                    >▲</button>
                    <button
                      onClick={() => moveCat(cat, 1)}
                      disabled={catIdx === categorias.length - 1}
                      title="Bajar sección"
                      style={{ padding: '2px 7px', borderRadius: 4, border: '1px solid #E5E7EB', background: catIdx === categorias.length - 1 ? '#F9FAFB' : '#fff', fontSize: 11, cursor: catIdx === categorias.length - 1 ? 'default' : 'pointer', color: catIdx === categorias.length - 1 ? '#D1D5DB' : '#6B7280' }}
                    >▼</button>
                  </div>
                  <button
                    onClick={() => { setEditingCat(cat); setEditCatName(cat) }}
                    title="Editar nombre"
                    style={{ padding: '2px 7px', borderRadius: 6, border: '1px solid #E5E7EB', background: '#fff', fontSize: 11, cursor: 'pointer', color: '#6B7280' }}
                  >✏️</button>
                  <button
                    onClick={() => {
                      const msg = catItems.length > 0
                        ? `¿Eliminar la categoría "${cat}"? Sus ${catItems.length} ítem(s) se moverán a la siguiente categoría disponible.`
                        : `¿Eliminar la categoría "${cat}"?`
                      if (window.confirm(msg)) deleteCat(cat)
                    }}
                    title="Eliminar categoría"
                    style={{ padding: '2px 7px', borderRadius: 6, border: '1px solid #FCA5A5', background: '#FEF2F2', fontSize: 11, cursor: 'pointer', color: '#EF4444' }}
                  >🗑️</button>
                </>
              )}

              <div style={{ flex: 1 }} />
              <button
                onClick={() => { setAddingCat(cat); setNewDesc('') }}
                style={{
                  padding: '3px 10px', borderRadius: 6, border: '1px solid #E5E7EB',
                  background: '#fff', color: '#6B7280', fontSize: 12, cursor: 'pointer'
                }}>+ Agregar</button>
            </div>

            <div style={{ border: '1px solid #E5E7EB', borderRadius: 8, overflow: 'hidden' }}>
              {catItems.length === 0 && (
                <div style={{ padding: '12px 16px', fontSize: 13, color: '#9CA3AF', textAlign: 'center' }}>
                  Sin solicitudes en esta categoría
                </div>
              )}
              {catItems.map((item, idx) => (
                <div key={item.id} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  padding: '10px 14px',
                  background: idx % 2 === 0 ? '#fff' : '#FAFAFA',
                  borderBottom: idx < catItems.length - 1 ? '1px solid #F3F4F6' : 'none'
                }}>
                  {editingId === item.id ? (
                    <EditRow
                      form={editForm}
                      setForm={setEditForm}
                      onSave={saveEdit}
                      onCancel={() => setEditingId(null)}
                    />
                  ) : (
                    <>
                      {/* Checkbox-like status indicator */}
                      <div
                        onClick={() => {
                          const next = item.estado === 'Recibido' ? 'Pendiente' : 'Recibido'
                          updateEstado(item.id, next)
                        }}
                        style={{
                          width: 18, height: 18, borderRadius: 4, marginTop: 1, flexShrink: 0,
                          border: `2px solid ${item.estado === 'Recibido' ? '#10B981' : '#D1D5DB'}`,
                          background: item.estado === 'Recibido' ? '#10B981' : '#fff',
                          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: '#fff', fontSize: 11, fontWeight: 700
                        }}
                      >{item.estado === 'Recibido' ? '✓' : ''}</div>

                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, color: '#111827', lineHeight: 1.4 }}>{item.descripcion}</div>
                        {item.responsable && (
                          <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>👤 {item.responsable}</div>
                        )}
                        {item.fecha_limite && (
                          <div style={{ fontSize: 11, color: '#6B7280' }}>📅 {item.fecha_limite}</div>
                        )}
                        {item.notas && (
                          <div style={{ fontSize: 11, color: '#9CA3AF', fontStyle: 'italic', marginTop: 2 }}>{item.notas}</div>
                        )}
                        {item.razon_no_cierre && ESTADOS_FINAL_TERMINAL.includes(item.estado_final) && (
                          <div style={{ fontSize: 11, color: '#7F1D1D', fontStyle: 'italic', marginTop: 4, padding: '4px 8px', background: '#FEF2F2', borderLeft: '2px solid #DC2626', borderRadius: 3 }}>
                            ⚠️ {item.razon_no_cierre}
                          </div>
                        )}
                      </div>

                      {/* Estado inicial selector */}
                      <select
                        value={item.estado}
                        onChange={e => updateEstado(item.id, e.target.value)}
                        title="Estado inicial — al levantar la información"
                        style={{
                          padding: '3px 8px', borderRadius: 99, border: 'none', fontSize: 11,
                          fontWeight: 600, cursor: 'pointer',
                          background: ESTADO_COLORS[item.estado]?.bg || '#F3F4F6',
                          color: ESTADO_COLORS[item.estado]?.text || '#374151',
                        }}
                      >
                        {ESTADOS.map(e => <option key={e} value={e}>{e}</option>)}
                      </select>

                      {/* Estado final selector */}
                      <select
                        value={item.estado_final ?? '—'}
                        onChange={e => updateEstadoFinal(item.id, e.target.value)}
                        title="Estado final — destino al cerrar la fase"
                        style={{
                          padding: '3px 8px', borderRadius: 99, border: `1px dashed ${ESTADO_FINAL_COLORS[item.estado_final ?? '—']?.text || '#D1D5DB'}`,
                          fontSize: 11, fontWeight: 600, cursor: 'pointer',
                          background: ESTADO_FINAL_COLORS[item.estado_final ?? '—']?.bg || '#F3F4F6',
                          color: ESTADO_FINAL_COLORS[item.estado_final ?? '—']?.text || '#9CA3AF',
                        }}
                      >
                        {ESTADOS_FINAL.map(e => <option key={e} value={e}>{e}</option>)}
                      </select>

                      {/* Reorder buttons */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <button
                          onClick={() => moveItem(item.id, -1)}
                          disabled={idx === 0}
                          style={{ padding: '1px 6px', borderRadius: 4, border: '1px solid #E5E7EB', background: idx === 0 ? '#F9FAFB' : '#fff', fontSize: 10, cursor: idx === 0 ? 'default' : 'pointer', color: idx === 0 ? '#D1D5DB' : '#6B7280', lineHeight: 1.2 }}
                        >▲</button>
                        <button
                          onClick={() => moveItem(item.id, 1)}
                          disabled={idx === catItems.length - 1}
                          style={{ padding: '1px 6px', borderRadius: 4, border: '1px solid #E5E7EB', background: idx === catItems.length - 1 ? '#F9FAFB' : '#fff', fontSize: 10, cursor: idx === catItems.length - 1 ? 'default' : 'pointer', color: idx === catItems.length - 1 ? '#D1D5DB' : '#6B7280', lineHeight: 1.2 }}
                        >▼</button>
                      </div>

                      {/* Actions */}
                      <button
                        onClick={() => { setEditingId(item.id); setEditForm({ ...item }) }}
                        style={{ padding: '3px 8px', borderRadius: 6, border: '1px solid #E5E7EB', background: '#fff', fontSize: 11, cursor: 'pointer', color: '#6B7280' }}
                      >✏️</button>
                      <button
                        onClick={() => deleteItem(item.id)}
                        style={{ padding: '3px 8px', borderRadius: 6, border: '1px solid #FCA5A5', background: '#FEF2F2', fontSize: 11, cursor: 'pointer', color: '#EF4444' }}
                      >✕</button>
                    </>
                  )}
                </div>
              ))}

              {/* Add row inline */}
              {addingCat === cat && (
                <div style={{ padding: '10px 14px', background: '#F0F9FF', borderTop: '1px solid #BAE6FD', display: 'flex', gap: 8 }}>
                  <input
                    autoFocus
                    value={newDesc}
                    onChange={e => setNewDesc(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') addItem(cat); if (e.key === 'Escape') setAddingCat(null) }}
                    placeholder="Descripción de la solicitud…"
                    style={{
                      flex: 1, padding: '6px 10px', borderRadius: 6, border: '1px solid #BAE6FD',
                      fontSize: 13, outline: 'none', background: '#fff'
                    }}
                  />
                  <button onClick={() => addItem(cat)} style={{
                    padding: '6px 14px', borderRadius: 6, border: 'none',
                    background: '#0284C7', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer'
                  }}>Agregar</button>
                  <button onClick={() => setAddingCat(null)} style={{
                    padding: '6px 10px', borderRadius: 6, border: '1px solid #E5E7EB',
                    background: '#fff', color: '#6B7280', fontSize: 12, cursor: 'pointer'
                  }}>✕</button>
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function StatPill({ label, value, color }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 18, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 11, color: '#9CA3AF' }}>{label}</div>
    </div>
  )
}

function EditRow({ form, setForm, onSave, onCancel }) {
  const inp = { padding: '4px 8px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 12, outline: 'none' }
  const isTerminal = ESTADOS_FINAL_TERMINAL.includes(form.estado_final)
  const razonMissing = isTerminal && !form.razon_no_cierre?.trim()

  return (
    <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      <input value={form.descripcion || ''} onChange={e => setForm(f => ({...f, descripcion: e.target.value}))} style={{ ...inp, flex: '1 1 200px' }} placeholder="Descripción" />
      <input value={form.responsable || ''} onChange={e => setForm(f => ({...f, responsable: e.target.value}))} style={{ ...inp, width: 120 }} placeholder="Responsable" />
      <input type="date" value={form.fecha_limite || ''} onChange={e => setForm(f => ({...f, fecha_limite: e.target.value || null}))} style={{ ...inp, width: 130 }} />
      <input value={form.notas || ''} onChange={e => setForm(f => ({...f, notas: e.target.value}))} style={{ ...inp, flex: '1 1 150px' }} placeholder="Notas" />

      {/* Razón obligatoria si estado final es terminal — full width abajo */}
      {isTerminal && (
        <div style={{ flexBasis: '100%', display: 'flex', flexDirection: 'column', gap: 4, padding: '8px 0', marginTop: 4, borderTop: '1px dashed #FCA5A5' }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: razonMissing ? '#DC2626' : '#92400E' }}>
            ⚠️ Razón de no-cierre (obligatorio para "{form.estado_final}")
          </label>
          <textarea
            value={form.razon_no_cierre || ''}
            onChange={e => setForm(f => ({...f, razon_no_cierre: e.target.value}))}
            placeholder="Explica por qué este item no se va a cerrar. Esta razón aparece en el reporte que se entrega al cliente."
            rows={2}
            style={{
              ...inp, width: '100%', resize: 'vertical', minHeight: 50,
              border: razonMissing ? '1px solid #DC2626' : '1px solid #D1D5DB',
              background: razonMissing ? '#FEF2F2' : '#fff'
            }}
          />
        </div>
      )}

      <button onClick={onSave} disabled={razonMissing} style={{ padding: '4px 12px', borderRadius: 6, border: 'none', background: razonMissing ? '#D1D5DB' : '#10B981', color: '#fff', fontSize: 12, cursor: razonMissing ? 'not-allowed' : 'pointer' }}>✓</button>
      <button onClick={onCancel} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #E5E7EB', background: '#fff', fontSize: 12, cursor: 'pointer' }}>✕</button>
    </div>
  )
}

// ─── Vista Cliente ────────────────────────────────────────────
function ClientView({ items, onBack }) {
  const pendientes = items.filter(i => i.estado === 'Pendiente' || i.estado === 'En revisión')
  const recibidos  = items.filter(i => i.estado === 'Recibido')
  const noAplica   = items.filter(i => i.estado === 'No aplica')
  const total      = items.length
  const pct        = total > 0 ? Math.round((recibidos.length / total) * 100) : 0

  function printView() { window.print() }

  return (
    <div>
      {/* Header toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button onClick={onBack} style={{
          padding: '6px 14px', borderRadius: 8, border: '1px solid #E5E7EB',
          background: '#fff', color: '#374151', fontSize: 13, cursor: 'pointer'
        }}>← Volver</button>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>
            Lista de Requerimientos de Información (DRL)
          </div>
          <div style={{ fontSize: 12, color: '#9CA3AF' }}>Vista para compartir con el cliente</div>
        </div>
        <button onClick={printView} style={{
          padding: '6px 14px', borderRadius: 8, border: '1px solid #E5E7EB',
          background: '#fff', color: '#374151', fontSize: 12, cursor: 'pointer'
        }}>🖨️ Imprimir / PDF</button>
      </div>

      {/* Progress summary */}
      <div style={{
        display: 'flex', gap: 20, padding: '16px 20px', marginBottom: 24,
        background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 12, alignItems: 'center'
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#065F46', marginBottom: 6 }}>
            Progreso general de entrega
          </div>
          <div style={{ height: 10, background: '#D1FAE5', borderRadius: 99 }}>
            <div style={{ width: `${pct}%`, height: '100%', background: '#10B981', borderRadius: 99, transition: 'width 0.4s' }} />
          </div>
        </div>
        <div style={{ textAlign: 'center', minWidth: 60 }}>
          <div style={{ fontSize: 24, fontWeight: 800, color: '#10B981' }}>{pct}%</div>
          <div style={{ fontSize: 11, color: '#6B7280' }}>Completado</div>
        </div>
        <div style={{ display: 'flex', gap: 16 }}>
          {[
            { label: 'Recibido', value: recibidos.length, color: '#10B981' },
            { label: 'Pendiente', value: pendientes.length, color: '#F59E0B' },
            { label: 'No aplica', value: noAplica.length, color: '#6B7280' },
          ].map(s => (
            <div key={s.label} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 11, color: '#9CA3AF' }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Categories */}
      {CATEGORIAS.map(cat => {
        const catItems = items.filter(i => i.categoria === cat)
        if (catItems.length === 0) return null
        const catRecibidos = catItems.filter(i => i.estado === 'Recibido').length
        const catPct = Math.round((catRecibidos / catItems.length) * 100)

        return (
          <div key={cat} style={{ marginBottom: 20 }}>
            {/* Category header */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '10px 14px', background: '#F9FAFB',
              border: '1px solid #E5E7EB', borderRadius: '8px 8px 0 0',
              borderBottom: 'none'
            }}>
              <span style={{ fontSize: 15 }}>{CAT_ICONS[cat]}</span>
              <span style={{ fontWeight: 700, fontSize: 14, color: '#111827', flex: 1 }}>{cat}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 80, height: 5, background: '#E5E7EB', borderRadius: 99 }}>
                  <div style={{ width: `${catPct}%`, height: '100%', background: '#10B981', borderRadius: 99 }} />
                </div>
                <span style={{ fontSize: 11, color: '#6B7280', minWidth: 32 }}>{catPct}%</span>
              </div>
            </div>

            {/* Items table */}
            <div style={{ border: '1px solid #E5E7EB', borderRadius: '0 0 8px 8px', overflow: 'hidden' }}>
              {catItems.map((item, idx) => {
                const estCfg = {
                  'Pendiente':   { dot: '#F59E0B', label: 'Pendiente' },
                  'En revisión': { dot: '#3B82F6', label: 'En revisión' },
                  'Recibido':    { dot: '#10B981', label: 'Recibido ✓' },
                  'No aplica':   { dot: '#9CA3AF', label: 'No aplica' },
                }[item.estado] || { dot: '#9CA3AF', label: item.estado }

                return (
                  <div key={item.id} style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 14px',
                    background: idx % 2 === 0 ? '#fff' : '#FAFAFA',
                    borderBottom: idx < catItems.length - 1 ? '1px solid #F3F4F6' : 'none'
                  }}>
                    <div style={{
                      width: 8, height: 8, borderRadius: '50%',
                      background: estCfg.dot, flexShrink: 0
                    }} />
                    <div style={{ flex: 1, fontSize: 13, color: '#374151', lineHeight: 1.4 }}>
                      {item.descripcion}
                      {item.notas && <span style={{ fontSize: 11, color: '#9CA3AF', marginLeft: 8, fontStyle: 'italic' }}>{item.notas}</span>}
                    </div>
                    {item.responsable && (
                      <div style={{ fontSize: 11, color: '#6B7280', whiteSpace: 'nowrap' }}>
                        👤 {item.responsable}
                      </div>
                    )}
                    {item.fecha_limite && (
                      <div style={{ fontSize: 11, color: '#6B7280', whiteSpace: 'nowrap' }}>
                        📅 {item.fecha_limite}
                      </div>
                    )}
                    <span style={{
                      fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
                      color: estCfg.dot
                    }}>{estCfg.label}</span>
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}

      {/* ── Información NO incluida y justificación (cierre limpio) ───── */}
      {(() => {
        const noIncluidos = items.filter(i =>
          i.estado !== 'No aplica' &&
          i.estado !== 'Recibido' &&
          (!i.estado_final || ESTADOS_FINAL_TERMINAL.includes(i.estado_final))
        )
        if (noIncluidos.length === 0) return null
        return (
          <div style={{
            marginTop: 32, padding: '20px 24px', background: '#FFFBEB',
            border: '1px solid #FCD34D', borderRadius: 12
          }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#78350F', marginBottom: 6 }}>
              ⚠️ Información no incluida en el plan final
            </div>
            <p style={{ fontSize: 12, color: '#92400E', marginBottom: 16, lineHeight: 1.5 }}>
              Los siguientes {noIncluidos.length} item{noIncluidos.length !== 1 ? 's' : ''} no quedaron
              cerrados positivamente al final del proyecto. Cada uno tiene su justificación documentada.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {noIncluidos.map(item => (
                <div key={item.id} style={{ background: '#fff', padding: '12px 14px', borderRadius: 8, border: '1px solid #FDE68A' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#78350F' }}>
                    • {item.descripcion}
                  </div>
                  <div style={{ fontSize: 11, color: '#92400E', marginTop: 2 }}>
                    {item.categoria}
                    {item.estado_final
                      ? <> · Decisión: <strong>{item.estado_final}</strong></>
                      : <> · Estado: <strong>{item.estado}</strong> (sin decisión final)</>
                    }
                  </div>
                  {item.razon_no_cierre && (
                    <div style={{ fontSize: 12, color: '#7F1D1D', fontStyle: 'italic', marginTop: 8, lineHeight: 1.5, paddingLeft: 12, borderLeft: '2px solid #DC2626' }}>
                      "{item.razon_no_cierre}"
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )
      })()}

      <div style={{ marginTop: 24, padding: '12px 16px', background: '#F9FAFB', borderRadius: 8, fontSize: 12, color: '#9CA3AF', textAlign: 'center' }}>
        Generado por Next Valley Consultants · {new Date().toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' })}
      </div>
    </div>
  )
}
